import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { applyBoardingPoint, fallbackInfoLines, loadLogoDataUrl } from '../utils/ticketPDF';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ordinal = n => { const s=['th','st','nd','rd']; const v=n%100; return n+(s[(v-20)%10]||s[v]||s[0]); };
const fmtDate = ddmmyyyy => {
  if (!ddmmyyyy) return '—';
  const [dd,mm,yyyy] = ddmmyyyy.split('-');
  return `${ordinal(parseInt(dd,10))} ${MONTHS_SHORT[parseInt(mm,10)-1]} ${yyyy}`;
};
// Exported so the WhatsApp send flow formats reporting time identically to the
// printed ticket. Behaviour here is unchanged.
export const to12hr = hhmm => {
  if (!hhmm) return '—';
  const [h,m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2,'0')} ${period}`;
};

function GoldDivider() {
  return <div style={{ height: 2, background: '#C9A84C' }} />;
}

// ─── Ticket content — exported so TicketPreview can use it for live preview ──
export const TicketContent = React.forwardRef(function TicketContent(
  { booking, reference_number, cruise_settings, ticket_settings, qr_code_data_url, logoDataUrl, cruise_display_name },
  ref
) {
  // Guest-facing long name (utils/cruiseNames); the stored cruise_type is the
  // fallback and stays the key everywhere else.
  const cruiseName = (cruise_display_name || '').trim() || booking?.cruise_type || '—';
  const boardingPoint = ticket_settings?.boarding_point || 'Guwahati';

  const importantInfo = ticket_settings?.instructions
    ? applyBoardingPoint(ticket_settings.instructions.split('\n').filter(s => s.trim()), boardingPoint)
    : fallbackInfoLines(boardingPoint);

  const paxItems = [
    { label: 'ADULTS',       n: Number(booking?.adults       || 0), always: true  },
    { label: 'CHILDREN',     n: Number(booking?.children     || 0), always: false },
    { label: 'INFANTS',      n: Number(booking?.infants      || 0), always: false },
    { label: 'TOUR LEADERS', n: Number(booking?.tour_leaders || 0), always: false },
  ].filter(f => f.always || f.n > 0);

  return (
    <div ref={ref} style={{
      width: '210mm',
      background: '#ffffff',
      fontFamily: 'Helvetica, Arial, sans-serif',
      boxSizing: 'border-box',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }}>

      {/* ── Header ── */}
      <div style={{ background: '#0A1628', padding: '14px 20px' }}>
        <img
          src={logoDataUrl || '/alfresco-grand.png'}
          alt="Alfresco Grand"
          style={{
            height: 28,
            display: 'block',
            ...(logoDataUrl ? {} : { mixBlendMode: 'screen' }),
          }}
        />
        <div style={{
          color: '#C9A84C',
          fontSize: 14,
          fontWeight: 'bold',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          marginTop: 8,
        }}>
          BOARDING PASS
        </div>
      </div>

      {/* ── Body section 1 — two columns ── */}
      <div style={{ padding: '16px 20px', display: 'flex' }}>

        {/* Left 70% */}
        <div style={{ width: '70%', paddingRight: 12, boxSizing: 'border-box' }}>
          <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
            BOOKING REFERENCE
          </div>
          <div style={{ color: '#0A1628', fontSize: 18, fontWeight: 'bold', marginBottom: 6 }}>
            {reference_number || '—'}
          </div>
          <div style={{ height: 1, background: '#e0e0e0', marginBottom: 6 }} />

          <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
            PASSENGER
          </div>
          <div style={{ color: '#0A1628', fontSize: 24, fontWeight: 'bold', marginBottom: 6 }}>
            {(booking?.booking_name || '').toUpperCase()}
          </div>
          <div style={{ height: 1, background: '#e0e0e0', marginBottom: 6 }} />

          {/* CRUISE | DECK | DATE — the cruise cell wraps (a long display name
              takes two lines at the same 18px); deck and date never need more
              than their share, so the cruise column carries the slack. */}
          <div style={{ display: 'flex' }}>
            {[
              { label: 'CRUISE', value: cruiseName,           width: '42%' },
              { label: 'DECK',   value: booking?.deck || '—', width: '28%' },
              { label: 'DATE',   value: fmtDate(booking?.date), width: '30%' },
            ].map(({ label, value, width }) => (
              <div key={label} style={{ width, paddingRight: 8, boxSizing: 'border-box', overflow: 'hidden' }}>
                <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 3 }}>
                  {label}
                </div>
                <div style={{ color: '#0A1628', fontSize: 18, fontWeight: 'bold', lineHeight: 1.15, overflowWrap: 'break-word' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right 30% — QR */}
        <div style={{ width: '30%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>
            SCAN TO VERIFY
          </div>
          {qr_code_data_url ? (
            <img src={qr_code_data_url} alt="QR Code" style={{ width: 90, height: 90, display: 'block' }} />
          ) : (
            <div style={{ width: 90, height: 90, background: '#f5f5f5', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#aaa' }}>
              QR
            </div>
          )}
          <div style={{ color: '#0A1628', fontSize: 6, marginTop: 4, textAlign: 'right' }}>
            {reference_number}
          </div>
        </div>
      </div>

      {/* ── Body section 2 ── */}
      <div style={{ padding: '16px 20px' }}>

        {/* Times */}
        <div style={{ display: 'flex', marginBottom: 8 }}>
          {[
            { label: 'REPORT BY', value: to12hr(cruise_settings?.reporting_time) },
            { label: 'DEPARTURE', value: to12hr(cruise_settings?.departure_time) },
            { label: 'END TIME',  value: to12hr(cruise_settings?.end_time) },
          ].map(({ label, value }) => (
            <div key={label} style={{ flex: 1 }}>
              <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 3 }}>
                {label}
              </div>
              <div style={{ color: '#0A1628', fontSize: 18, fontWeight: 'bold' }}>
                {value}
              </div>
            </div>
          ))}
        </div>
        <div style={{ height: 1, background: '#e0e0e0', marginBottom: 8 }} />

        {/* Pax */}
        <div style={{ display: 'flex', marginBottom: 8 }}>
          {paxItems.map(({ label, n }) => (
            <div key={label} style={{ flex: 1 }}>
              <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 3 }}>
                {label}
              </div>
              <div style={{ color: '#0A1628', fontSize: 18, fontWeight: 'bold' }}>
                {n}
              </div>
            </div>
          ))}
        </div>
        <div style={{ height: 1, background: '#e0e0e0', marginBottom: 8 }} />

        {/* Boarding point */}
        <div style={{ color: '#C9A84C', fontSize: 7, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 3 }}>
          BOARDING POINT
        </div>
        <div style={{ color: '#0A1628', fontSize: 14, fontWeight: 'bold' }}>
          {boardingPoint}
        </div>
      </div>

      <GoldDivider />

      {/* ── Important Information ── */}
      <div style={{ padding: '16px 20px' }}>
        <div style={{ color: '#0A1628', fontSize: 9, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>
          IMPORTANT INFORMATION
        </div>
        {importantInfo.map((line, i) => (
          <div key={i} style={{ color: '#0A1628', fontSize: 9, lineHeight: 1.8, marginBottom: 8 }}>
            {i + 1}.{line}
          </div>
        ))}
      </div>

    </div>
  );
});

// ─── Modal — preview + direct PDF download ───────────────────────────────────
const PREVIEW_SCALE = 0.52;

export default function TicketPrint({
  booking, reference_number, cruise_settings, ticket_settings, qr_code_data_url, onClose,
}) {
  const contentRef   = useRef(null);
  const [wrapperHeight, setWrapperHeight] = useState(null);
  const [downloading, setDownloading]     = useState(false);
  const [logoDataUrl, setLogoDataUrl]     = useState(null);

  // Pre-process logo so html2canvas renders it correctly (no mix-blend-mode needed)
  useEffect(() => {
    loadLogoDataUrl().then(result => {
      if (result?.dataUrl) setLogoDataUrl(result.dataUrl);
    });
  }, []);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setWrapperHeight(contentRef.current.offsetHeight * PREVIEW_SCALE);
    }
  }, [logoDataUrl]); // re-measure once logo loads

  const handleDownload = async () => {
    if (!contentRef.current || downloading) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const scaledH = (canvas.height / canvas.width) * pdfW;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, scaledH);
      const safeName = (booking.booking_name || 'Guest')
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .trim()
        .replace(/\s+/g, '-');
      pdf.save(`ticket-${reference_number || 'ref'}-${safeName}-${booking.date || 'date'}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <style>{`
        .tp-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(10,22,40,0.72);
          display: flex; align-items: flex-start; justify-content: center;
          padding: 24px 16px; overflow-y: auto;
        }
        .tp-modal {
          background: #fff; border-radius: 14px;
          box-shadow: 0 8px 40px rgba(10,22,40,0.35);
          width: 100%; max-width: 500px; overflow: hidden;
        }
        .tp-bar {
          background: #0A1628; padding: 14px 20px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .tp-preview { background: #e8e4dc; padding: 16px; overflow-x: auto; }
      `}</style>

      <div className="tp-overlay" onClick={onClose}>
        <div className="tp-modal" onClick={e => e.stopPropagation()}>

          <div className="tp-bar">
            <span style={{ color: '#C9A84C', fontWeight: 700, fontSize: '0.88rem', letterSpacing: '0.3px' }}>
              Ticket Preview
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleDownload}
                disabled={downloading}
                style={{
                  background: downloading ? 'rgba(201,168,76,0.7)' : '#C9A84C',
                  color: '#0A1628', border: 'none', borderRadius: 7,
                  padding: '7px 16px', fontSize: '0.82rem', fontWeight: 700,
                  cursor: downloading ? 'not-allowed' : 'pointer',
                  minWidth: 130,
                }}
              >
                {downloading ? 'Downloading…' : 'Download Ticket'}
              </button>
              <button
                onClick={onClose}
                style={{ background: 'transparent', color: '#a09070', border: '1.5px solid #a0907044', borderRadius: 7, padding: '7px 12px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>

          <div className="tp-preview">
            <div style={{
              position: 'relative',
              width: `calc(210mm * ${PREVIEW_SCALE})`,
              height: wrapperHeight ? `${wrapperHeight}px` : 'auto',
              overflow: 'hidden',
              margin: '0 auto',
            }}>
              <div style={{ transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left', width: '210mm' }}>
                <TicketContent
                  ref={contentRef}
                  booking={booking}
                  reference_number={reference_number}
                  cruise_settings={cruise_settings}
                  ticket_settings={ticket_settings}
                  qr_code_data_url={qr_code_data_url}
                  logoDataUrl={logoDataUrl}
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
